/**
 * Shop categories: a few top-level categories, each grouping related subcategories.
 * Products are assigned a subcategory. Product `category` values from the API are free text
 * (and historically inconsistent, e.g. "NVR & Recoders", "Power&Accessories"), so each
 * subcategory lists the patterns it owns.
 */

/** Top-level categories, in display order. */
export const SHOP_CATEGORIES = [
    {
        id: 'starlink',
        name: 'Starlink Equipment',
        description: 'Satellite internet kits, routers and accessories',
        icon: 'fa-satellite-dish',
        image: '/assets/img/shop/categories/starlink-equipment.png',
    },
    {
        id: 'digital-security',
        name: 'Digital Security',
        description: 'CCTV cameras, recorders, alarms and intercoms',
        icon: 'fa-shield-halved',
        image: '/assets/img/shop/categories/cctv-kits.png',
    },
    {
        id: 'power-backup',
        name: 'Power Backup',
        description: 'UPS systems that keep your equipment running',
        icon: 'fa-plug-circle-bolt',
        image: '/assets/img/shop/categories/power-accessories.png',
    },
]

/**
 * Groups that classify products. A group whose id equals its parent's id is the category's "general"
 * group: Starlink Equipment and Power Backup have only that one (no sub-categories shown); Digital
 * Security has sub-categories plus a general group for anything that fits none of them (e.g. alarms).
 * `match` patterns are tested against the product's saved category, then (for general groups) its name.
 */
export const SHOP_SUBCATEGORIES = [
    {
        id: 'starlink',
        parent: 'starlink',
        name: 'Starlink Equipment',
        description: 'Satellite internet kits, routers and accessories',
        icon: 'fa-satellite-dish',
        match: [/starlink/i, /satellite/i],
        image: '/assets/img/shop/categories/starlink-equipment.png',
    },
    {
        id: 'cctv-cameras',
        parent: 'digital-security',
        name: 'CCTV Cameras',
        description: 'Dome, bullet, turret and PTZ cameras',
        icon: 'fa-video',
        match: [/cctv/i, /camera/i, /\bdome\b/i, /bullet/i, /turret/i, /\bptz\b/i],
        image: '/assets/img/shop/categories/cctv-cameras.png',
    },
    {
        id: 'cctv-kits',
        parent: 'digital-security',
        name: 'CCTV Camera Kits',
        description: 'Cameras + recorder, ready to install',
        icon: 'fa-boxes-packing',
        match: [/(cctv|camera|\bcam\b|\bnvr\b|\bnvs\b|\bdvr\b|turbo|\d+\s*-?\s*ch\b).*\bkits?\b/i, /\bkits?\b.*(cctv|camera)/i],
        image: '/assets/img/shop/categories/cctv-kits.png',
    },
    {
        id: 'dvr-recorders',
        parent: 'digital-security',
        name: 'Digital Video Recorders (DVR)',
        description: 'Recorders for analogue / Turbo HD cameras',
        icon: 'fa-hard-drive',
        match: [/\bdvr\b/i, /digital video recorder/i, /\bxvr\b/i],
        image: '/assets/img/shop/categories/nvr-recorders.png',
    },
    {
        id: 'nvr-recorders',
        parent: 'digital-security',
        name: 'Network Video Recorders (NVR)',
        description: 'Recorders for IP / network cameras',
        icon: 'fa-server',
        match: [/\bnvr\b/i, /\bnvs\b/i, /network video recorder/i, /recod/i],
        image: '/assets/img/shop/categories/nvr-recorders.png',
    },
    {
        id: 'gate-automation',
        parent: 'digital-security',
        name: 'Gate Automation',
        description: 'Gate openers, barriers and motors',
        icon: 'fa-torii-gate',
        match: [/\bgate\b/i, /barrier/i, /\bboom\b/i, /opener/i],
        image: '',
    },
    {
        id: 'storage',
        parent: 'digital-security',
        name: 'Storage',
        description: 'Surveillance hard drives and memory cards',
        icon: 'fa-database',
        match: [/storage/i, /hard\s*(disk|drive)/i, /\bhdd\b/i, /\bssd\b/i, /micro\s*sd/i, /\bsd\s*card/i, /memory\s*card/i],
        image: '',
    },
    {
        id: 'access-control',
        parent: 'digital-security',
        name: 'Access Control / Time Attendance',
        description: 'Access terminals, attendance, intercoms and door stations',
        icon: 'fa-fingerprint',
        match: [/access\s*control/i, /attendance/i, /biometric/i, /fingerprint/i, /face\s*recognition/i, /intercom/i, /door\s*(bell|phone|station)/i, /turnstile/i],
        image: '',
    },
    {
        id: 'security-inspection',
        parent: 'digital-security',
        name: 'Security Inspection',
        description: 'Walk-through and hand-held metal detectors, X-ray baggage scanners',
        icon: 'fa-person-walking-luggage',
        match: [/inspection/i, /metal\s*detect/i, /walk[\s-]*through/i, /x[\s-]*ray/i, /baggage/i, /luggage/i, /under[\s-]*vehicle/i, /explosive/i],
        image: '',
    },
    {
        id: 'digital-security',
        parent: 'digital-security',
        name: 'Digital Security',
        description: 'CCTV cameras, recorders, gate automation, storage, access control and security inspection',
        icon: 'fa-shield-halved',
        match: [/digital\s*security/i, /security/i, /surveillance/i, /alarm/i, /intrusion/i],
        image: '/assets/img/shop/categories/cctv-kits.png',
    },
    {
        id: 'power-backup',
        parent: 'power-backup',
        name: 'Power Backup',
        description: 'UPS systems that keep your equipment running',
        icon: 'fa-plug-circle-bolt',
        match: [/power/i, /\bups\b/i, /batter/i, /inverter/i, /surge/i],
        image: '/assets/img/shop/categories/power-accessories.png',
    },
]

/** Old sub-category ids (links that may still exist) → the group that now holds those products. */
export const LEGACY_CATEGORY_IDS = {
    'starlink-equipment': 'starlink',
    'starlink-accessories': 'starlink',
    'alarm-systems': 'digital-security',
    'intercom-systems': 'access-control',
    'power-accessories': 'power-backup',
}

// Order in which groups are tested (first match wins): specific security groups before general ones
const MATCH_PRIORITY = [
    'starlink',
    'cctv-kits',
    'nvr-recorders',
    'dvr-recorders',
    'gate-automation',
    'storage',
    'access-control',
    'security-inspection',
    'cctv-cameras',
    'digital-security',
    'power-backup',
]

/** True for a category's general group (shares the category's id; not shown as a sub-category). */
export const isGeneralGroup = (sub) => Boolean(sub) && sub.id === sub.parent

const PARENT_BY_ID = Object.fromEntries(SHOP_CATEGORIES.map((category) => [category.id, category]))
const SUB_BY_ID = Object.fromEntries(SHOP_SUBCATEGORIES.map((sub) => [sub.id, sub]))
const SUBS_BY_PRIORITY = MATCH_PRIORITY.map((id) => SUB_BY_ID[id])
const PARENT_ORDER = SHOP_CATEGORIES.map((category) => category.id)
const SUB_ORDER = SHOP_SUBCATEGORIES.map((sub) => sub.id)

/** Subcategories grouped by category, for admin pickers. */
export const CATEGORY_GROUPS = SHOP_CATEGORIES.map((category) => ({
    ...category,
    subcategories: SHOP_SUBCATEGORIES.filter((sub) => sub.parent === category.id),
}))

export function toCategorySlug(value = '') {
    return String(value)
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

function prettifyRaw(raw = '') {
    return String(raw)
        .replace(/&/g, ' & ')
        .split(/[\s\-_]+/)
        .filter(Boolean)
        .map((part) => {
            const upper = part.toUpperCase()
            if (['CCTV', 'NVR', 'DVR', 'UPS', 'HD', 'IP', 'PTZ', 'POE'].includes(upper)) return upper
            if (part === '&') return part
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        })
        .join(' ')
}

/** Canonical subcategory for a raw API value, subcategory id or subcategory name. */
export function resolveShopCategory(raw = '') {
    const value = String(raw || '').trim()
    if (!value) return null
    const exact =
        SUB_BY_ID[value.toLowerCase()] ||
        SHOP_SUBCATEGORIES.find((sub) => sub.name.toLowerCase() === value.toLowerCase())
    if (exact) return exact
    return SUBS_BY_PRIORITY.find((sub) => sub.match.some((re) => re.test(value))) || null
}

export function getParentCategory(id = '') {
    return PARENT_BY_ID[id] || null
}

/**
 * Category + subcategory for a raw category value (and optionally the product name, used to place
 * products saved only under a category into its sub-category). Unknown values become their own
 * category with a stable slug.
 */
export function classifyCategory(raw = '', productName = '') {
    let sub = resolveShopCategory(raw)
    // Saved only as the category (e.g. "Digital Security"): use the product name to find its sub-category
    if (sub && isGeneralGroup(sub) && productName) {
        const refined = SUBS_BY_PRIORITY.find(
            (candidate) => candidate.parent === sub.parent && !isGeneralGroup(candidate) && candidate.match.some((re) => re.test(productName))
        )
        if (refined) sub = refined
    }
    if (sub) {
        const parent = PARENT_BY_ID[sub.parent]
        return {
            categoryId: parent.id,
            categoryName: parent.name,
            subcategoryId: sub.id,
            subcategoryName: sub.name,
        }
    }
    const id = toCategorySlug(raw) || 'uncategorised'
    const name = raw ? prettifyRaw(raw) : 'Uncategorised'
    return { categoryId: id, categoryName: name, subcategoryId: id, subcategoryName: name }
}

/** Most specific label for a raw category value (the subcategory name). */
export function formatCategoryLabel(raw = '') {
    return classifyCategory(raw).subcategoryName
}

/** Collection URL for a category id, subcategory id, or raw category value. */
export function collectionPath(idOrRaw = '', fallbackName = '') {
    if (PARENT_BY_ID[idOrRaw]) return `/collections/${idOrRaw}`
    const sub = resolveShopCategory(idOrRaw) || resolveShopCategory(fallbackName)
    if (sub) return `/collections/${sub.id}`
    return `/collections/${toCategorySlug(idOrRaw || fallbackName)}`
}

/** True when a product with this raw category belongs to collection `slug` (category or subcategory). */
export function matchCategoryBySlug(slug, rawCategory) {
    if (!slug) return false
    const { categoryId, subcategoryId } = classifyCategory(rawCategory)
    return categoryId === slug || subcategoryId === slug || toCategorySlug(rawCategory) === slug
}

/**
 * Categories that currently have products, in catalogue order, each with the subcategories
 * that have products. `productImage` is a product photo for entries without artwork.
 */
export function buildCategoryTree(products = [], getRaw = (product) => product.category, getName = (product) => product.title || product.name || '') {
    const parents = new Map()

    products.forEach((product) => {
        const { categoryId, categoryName, subcategoryId, subcategoryName } = classifyCategory(getRaw(product), getName(product))
        if (!parents.has(categoryId)) {
            const canonical = PARENT_BY_ID[categoryId]
            parents.set(categoryId, {
                id: categoryId,
                name: categoryName,
                description: canonical?.description || '',
                icon: canonical?.icon || 'fa-box',
                image: canonical?.image || '',
                count: 0,
                productImage: '',
                subcategories: new Map(),
            })
        }
        const parent = parents.get(categoryId)
        parent.count += 1
        if (!parent.image && !parent.productImage && product.image) parent.productImage = product.image

        if (!parent.subcategories.has(subcategoryId)) {
            const canonical = SUB_BY_ID[subcategoryId]
            parent.subcategories.set(subcategoryId, {
                id: subcategoryId,
                parent: categoryId,
                name: subcategoryName,
                description: canonical?.description || '',
                icon: canonical?.icon || parent.icon,
                image: canonical?.image || '',
                count: 0,
                productImage: '',
            })
        }
        const sub = parent.subcategories.get(subcategoryId)
        sub.count += 1
        if (!sub.image && !sub.productImage && product.image) sub.productImage = product.image
    })

    const rank = (order, id) => {
        const index = order.indexOf(id)
        return index === -1 ? order.length : index
    }
    return [...parents.values()]
        .sort((a, b) => rank(PARENT_ORDER, a.id) - rank(PARENT_ORDER, b.id) || a.name.localeCompare(b.name))
        .map((parent) => ({
            ...parent,
            subcategories: [...parent.subcategories.values()].sort(
                (a, b) => rank(SUB_ORDER, a.id) - rank(SUB_ORDER, b.id) || a.name.localeCompare(b.name)
            ),
        }))
}

/** Locate a collection slug in a category tree: `{ category, subcategory }` (subcategory may be null). */
export function findInCategoryTree(tree = [], slug = '') {
    for (const category of tree) {
        if (category.id === slug) return { category, subcategory: null }
        const subcategory = category.subcategories.find((sub) => sub.id === slug)
        if (subcategory) return { category, subcategory }
    }
    const parent = PARENT_BY_ID[slug]
    if (parent) return { category: { ...parent, count: 0, subcategories: [] }, subcategory: null }
    const sub = SUB_BY_ID[slug]
    if (sub) {
        const owner = PARENT_BY_ID[sub.parent]
        return {
            category: { ...owner, count: 0, subcategories: [] },
            subcategory: { ...sub, count: 0 },
        }
    }
    return null
}

export function detectBrand(name = '', category = '') {
    const haystack = `${name} ${category}`
    if (/starlink/i.test(haystack)) return 'Starlink'
    if (/hikvision/i.test(haystack)) return 'Hikvision'
    if (/dahua/i.test(haystack)) return 'Dahua'
    if (/imou/i.test(haystack)) return 'Imou'
    if (/tplink|tp-link/i.test(haystack)) return 'TP-Link'
    if (/ubiquiti|unifi/i.test(haystack)) return 'Ubiquiti'
    if (/hikvision|ds-/i.test(haystack)) return 'Hikvision'
    if (/ezviz/i.test(haystack)) return 'EZVIZ'
    if (/reolink/i.test(haystack)) return 'Reolink'
    return 'Fabritech'
}
